/**
 * useAddressBook Hook
 * @see mobile: module-customer/src/modules/addresses/hooks/use-address-book.ts
 */

'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { type CustomerAddress, useCustomerAddressesQuery } from '../../../types/front.graphql';
import { checkSelectedAddressPresent, getDefaultOrFirstAddress } from '../utils';
import { customerStoreAPI } from '../../../stores';
import { isRegionCodeMatchStoreCode } from '@kidslabs/module-core';

/**
 * Get the default or first address that matches the current store region.
 * If currentStoreCode is not provided, falls back to getDefaultOrFirstAddress without filtering.
 * If no addresses match the region, falls back to getDefaultOrFirstAddress (any region).
 */
const getRegionMatchedDefaultAddress = (
    addresses: readonly CustomerAddress[],
    currentStoreCode?: string
): CustomerAddress | undefined => {
    if (!currentStoreCode) return getDefaultOrFirstAddress(addresses);

    const regionMatched = addresses.filter(a => {
        const regionCode = a.region?.region_code;
        return regionCode ? isRegionCodeMatchStoreCode(regionCode, currentStoreCode) : false;
    });

    if (regionMatched.length > 0) {
        return getDefaultOrFirstAddress(regionMatched);
    }

    // No addresses match current region → fall back to any address
    return getDefaultOrFirstAddress(addresses);
};

interface UseAddressBookProps {
    initialSelectedShippingAddress?: CustomerAddress;
    showOnlyDefault?: boolean;
    onAddressSelected?: (address: CustomerAddress) => void;
    onEditPress?: (address?: CustomerAddress) => void;
    onAddPress?: (addresses: CustomerAddress[]) => void;
    autoSelectDefault?: boolean;
    /**
     * Current store code for region validation.
     * When provided, auto-select will prefer addresses matching this store's region.
     */
    currentStoreCode?: string;
}

/**
 * Try to find a matching customer address from the list for a parent-provided address.
 * The parent address may not have an `id` (e.g., mapped from cart shipping address),
 * so we match by key fields: firstname + lastname + street + city.
 */
const findMatchingCustomerAddress = (
    parentAddress: CustomerAddress | undefined,
    customerAddresses: readonly CustomerAddress[]
): CustomerAddress | undefined => {
    if (!parentAddress || customerAddresses.length === 0) return undefined;

    // First try exact id match
    if (parentAddress.id) {
        const byId = customerAddresses.find(a => a.id === parentAddress.id);
        if (byId) return byId;
    }

    // Fallback: match by key address fields (for cart-mapped addresses without id)
    return customerAddresses.find(
        a =>
            a.firstname === parentAddress.firstname &&
            a.lastname === parentAddress.lastname &&
            a.city === parentAddress.city &&
            a.street?.[0] === parentAddress.street?.[0]
    );
};

export const useAddressBook = ({
    initialSelectedShippingAddress,
    showOnlyDefault,
    onAddressSelected,
    onEditPress: onEditPressProp,
    onAddPress,
    autoSelectDefault = true,
    currentStoreCode,
}: UseAddressBookProps) => {
    const { data, loading, error } = useCustomerAddressesQuery({
        fetchPolicy: 'cache-and-network',
    });
    const [selectedShippingAddress, setSelectedShippingAddress] = useState<CustomerAddress | undefined>(
        () => initialSelectedShippingAddress
    );
    const [hasUsedDefaultAddress, setHasUsedDefaultAddress] = useState(false);

    const addresses = useMemo(() => {
        const validAddress = data?.customer?.addresses?.filter((a): a is CustomerAddress => a !== null) ?? [];
        return [...validAddress];
    }, [data?.customer?.addresses]);

    const setDefaultOrFirstAddress = useCallback((customerAddress: CustomerAddress[]) => {
        const defaultOrFirstAddress = getDefaultOrFirstAddress(customerAddress);
        customerStoreAPI.actions.setDefaultOrFirstAddress(defaultOrFirstAddress);
    }, []);

    // Sync parent-provided address to local state.
    // When autoSelectDefault=false, try to resolve the parent address to a customer address
    // from the list (the parent address may lack an `id` if mapped from cart shipping address).
    useEffect(() => {
        setDefaultOrFirstAddress(addresses);
        if (initialSelectedShippingAddress && addresses.length > 0) {
            if (!autoSelectDefault) {
                // Resolve to a customer address with proper `id` for filtering
                const resolved = findMatchingCustomerAddress(initialSelectedShippingAddress, addresses);
                setSelectedShippingAddress(resolved ?? initialSelectedShippingAddress);
            } else {
                setSelectedShippingAddress(initialSelectedShippingAddress);
            }
        } else if (initialSelectedShippingAddress) {
            // Addresses not loaded yet — use the parent address as-is
            setSelectedShippingAddress(initialSelectedShippingAddress);
        } else if (!autoSelectDefault && addresses.length > 0) {
            // Parent hasn't set an address yet (store not initialized),
            // but addresses are loaded. Use default for display only
            // (onAddressSelected is NOT called — no mutation fires).
            const displayAddress = getDefaultOrFirstAddress(addresses);
            setSelectedShippingAddress(displayAddress);
        }
    }, [addresses, setDefaultOrFirstAddress, initialSelectedShippingAddress, autoSelectDefault]);

    useEffect(() => {
        if (!autoSelectDefault) return;

        if (showOnlyDefault && !hasUsedDefaultAddress && addresses.length > 0) {
            const defaultOrFirstAddress = getRegionMatchedDefaultAddress(addresses, currentStoreCode);

            if (!defaultOrFirstAddress) return;

            setSelectedShippingAddress(previousSelectedAddress =>
                previousSelectedAddress?.id === defaultOrFirstAddress.id
                    ? previousSelectedAddress
                    : defaultOrFirstAddress
            );

            onAddressSelected?.(defaultOrFirstAddress);
            setHasUsedDefaultAddress(true);
        }
    }, [addresses, showOnlyDefault, hasUsedDefaultAddress, onAddressSelected, autoSelectDefault, currentStoreCode]);

    useEffect(() => {
        // When autoSelectDefault=false, the parent controls the address selection.
        // Do NOT override — the parent-provided address may not have an `id`
        // (e.g., mapped from cart shipping address) and would fail the presence check.
        if (!autoSelectDefault) return;

        if (!addresses.length) return;

        if (!checkSelectedAddressPresent(selectedShippingAddress, addresses)) {
            const defaultOrFirstAddress = getRegionMatchedDefaultAddress(addresses, currentStoreCode);
            if (defaultOrFirstAddress && defaultOrFirstAddress.id !== selectedShippingAddress?.id) {
                setSelectedShippingAddress(defaultOrFirstAddress);
                onAddressSelected?.(defaultOrFirstAddress);
            }
        }
    }, [
        addresses,
        selectedShippingAddress?.id,
        onAddressSelected,
        selectedShippingAddress,
        autoSelectDefault,
        currentStoreCode,
    ]);

    const filteredAddresses = useMemo(() => {
        if (showOnlyDefault && selectedShippingAddress) {
            // Try by id first
            if (selectedShippingAddress.id) {
                const byId = addresses.filter(a => a.id === selectedShippingAddress.id);
                if (byId.length > 0) return byId;
            }

            // Fallback: find matching address by fields (for addresses without id, e.g., mapped from cart)
            const matched = findMatchingCustomerAddress(selectedShippingAddress, addresses);
            if (matched) return [matched];

            // Last resort: show default address if available
            const fallback = getDefaultOrFirstAddress(addresses);
            return fallback ? [fallback] : [];
        }
        return addresses;
    }, [addresses, showOnlyDefault, selectedShippingAddress]);

    const handleEditPress = useCallback(
        (address?: CustomerAddress) => {
            onEditPressProp?.(address);
        },
        [onEditPressProp]
    );

    const handleAddressSelected = useCallback(
        (address: CustomerAddress) => {
            onAddressSelected?.(address);
            setSelectedShippingAddress(address);
        },
        [onAddressSelected]
    );

    const handleAddPress = useCallback(() => {
        onAddPress?.(filteredAddresses);
    }, [onAddPress, filteredAddresses]);

    return {
        loading,
        error,
        filteredAddresses,
        selectedShippingAddress,
        handleEditPress,
        handleAddressSelected,
        handleAddPress,
        showOnlyDefault,
    };
};
